// UX80 (docs/build-briefs/UX80-operations-menu.md; DEC-298): the account menu
// under the signed in person's name as DATA. UX116: a group is now a hub screen
// (functionHubs.ts) and its items are the hub's buttons; this table is unchanged in
// shape. One table names every group, every
// row and the permission id that shows it; one filter turns that table and a
// permission check into what is on screen. No DOM and no session import, so a
// plain node:test file can import it (test/account-menu-model.test.ts) and
// ui/src/main.ts draws from it.
//
// Every decision here resolves a PERMISSION id, never a role name (CLAUDE.md,
// check:no-role-driven-gating). A row is hidden, never grayed, without its id.
// A group with no visible row is hidden. The ids are the ones in
// design/mockups/ux80-notes.md section 3, and each is an existing id in
// src/domain/permissions.ts: this item adds no permission.

/** CASH1-S1-U1: the row id of Cash drawer, hidden from the menu unless the location's cash setting is on or a drawer is open. */
export const CASH_DRAWER_ROW_ID = "cash-drawer";

export type MenuGroupId = "ordering" | "inventory" | "reports" | "shift";

/** How urgent a count is. Red needs a manager, orange is waiting work, blue is
 * in progress. A group row shows one badge: the most urgent of its rows. */
export type BadgeTone = "red" | "orange" | "blue";

export interface MenuItem {
  /** Stable key, also the badge lookup key. */
  readonly id: string;
  readonly label: string;
  /** The button's data-testid in ui/index.html. Existing test ids are kept
   * for every screen that moved, so specs only change their path. */
  readonly testId: string;
  /** Shown when the person holds ANY of these. */
  readonly permissions: readonly string[];
  /** The plain line under the row's label. Absent for a row that needs none. */
  readonly sub?: string;
  /** The short word this row adds to its group's hint line while visible. */
  readonly word?: string;
  /** A small heading above the row (Reports splits Sales from Stock). */
  readonly section?: string;
}

export interface MenuGroup {
  readonly id: MenuGroupId;
  readonly label: string;
  /** The group row's data-testid. */
  readonly testId: string;
  readonly items: readonly MenuItem[];
  /**
   * RPTLAYOUT1-F1-S2: a group that opens one screen straight, with no hub and no rows (Reports opens its home). It shows
   * for a person holding ANY of these permissions; `items` is empty. Permission ids only, never a role name.
   */
  readonly direct?: { readonly permissions: readonly string[]; readonly hint: string };
}

export const ACCOUNT_MENU_GROUPS: readonly MenuGroup[] = [
  {
    id: "ordering",
    label: "Ordering",
    testId: "account-menu-group-ordering",
    items: [
      {
        id: "what-to-order",
        label: "What to order",
        testId: "admin-ordering-what-to-order",
        permissions: ["purchasing.cart.manage"],
        sub: "This week's order, by vendor",
        word: "what to order",
      },
      {
        id: "open-orders",
        label: "Open orders",
        testId: "admin-ordering-open-orders",
        permissions: ["purchasing.order.view"],
        sub: "Drafts, sent and receiving",
        word: "open orders",
      },
      {
        id: "cart",
        label: "Purchasing cart",
        testId: "admin-purchasing-open-cart",
        permissions: ["purchasing.cart.manage"],
        sub: "Build and place orders",
        word: "cart",
      },
      {
        id: "orders",
        label: "Purchase orders",
        testId: "admin-purchasing-open-orders",
        permissions: ["purchasing.order.view"],
        sub: "Receive a delivery",
        word: "orders",
      },
      {
        id: "outside-order",
        label: "Record an outside purchase",
        testId: "admin-purchasing-open-outside-order",
        permissions: ["inv.receipt.record"],
        sub: "Not on a purchase order",
        word: "outside orders",
      },
      {
        id: "price-list",
        label: "Update prices from a file",
        testId: "account-menu-price-list",
        permissions: ["inv.vendor_price_list.import"],
        sub: "A vendor sent prices",
        word: "price files",
      },
    ],
  },
  {
    id: "inventory",
    label: "Inventory",
    testId: "account-menu-group-inventory",
    items: [
      {
        id: "stock-levels",
        label: "Stock levels",
        testId: "account-menu-stock-levels",
        permissions: ["inv.onhand.view"],
        sub: "On hand, low or 86'd",
        word: "stock levels",
      },
      {
        id: "count-stock",
        label: "Count stock",
        testId: "account-menu-count-stock",
        permissions: ["inv.count_session.run"],
        sub: "Start or resume",
        word: "counts",
      },
      {
        id: "past-counts",
        label: "Past counts",
        testId: "account-menu-past-counts",
        permissions: ["inv.count_session.run"],
        sub: "Closed counts",
        word: "counts",
      },
      // INVXFER1-S2: send stock to another location of the same owner, or receive what one sent.
      {
        id: "transfers",
        label: "Transfers",
        testId: "account-menu-transfers",
        permissions: ["inv.transfer.view"],
        sub: "Send or receive",
        word: "transfers",
      },
      {
        id: "starting-stock",
        label: "Set starting stock",
        testId: "account-menu-starting-stock",
        permissions: ["inv.movement.record"],
        sub: "Opening stock",
        word: "starting stock",
      },
      // RPTLAYOUT1-F1-S2: the STK1 86 list (DEC-269) lives with the stock, not among the reports it was parked beside.
      {
        id: "stock-86-list",
        label: "86 list",
        testId: "account-menu-stock-86-list",
        permissions: ["inv.stock_flag.record"],
        sub: "Out of stock now",
        word: "86 list",
      },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    testId: "account-menu-group-reports",
    items: [],
    // RPTLAYOUT1-F1-S2 (owner ruling 2026-10-19, "new replaces old"): Reports opens the Reports home, not a hub of rows. The
    // list is every permission that opens something on that home or one of the report screens it links to.
    direct: {
      permissions: [
        "report.sales_by_day.view",
        "report.sales_by_item.view",
        "report.tips_by_employee.view",
        "report.sales_tax.view",
        "report.menu_profitability.view",
        "report.stock_value.view",
        "report.cost_margin.view",
        "report.abc_spirits.view",
        "report.count_variance.view",
        "report.receipts_spend.view",
        "report.custom.view",
      ],
      hint: "Sales, stock, purchasing, custom",
    },
  },
  {
    id: "shift",
    label: "Shift and closeout",
    testId: "account-menu-group-shift",
    items: [
      {
        id: "closeout",
        label: "Closeout summary",
        testId: "admin-open-closeout-summary",
        permissions: ["report.closeout.view"],
        sub: "Totals, then finalize",
        word: "closeout",
      },
      {
        id: "void-history",
        label: "Void history",
        testId: "admin-open-void-history",
        permissions: ["report.void_history.view"],
        sub: "Drinks taken off",
        word: "voids",
      },
      {
        id: "manager-exceptions",
        label: "Manager exceptions",
        testId: "admin-open-manager-exceptions",
        permissions: ["admin.manager_exception.view"],
        sub: "Need a manager",
        word: "problems",
      },
      // CASH1-S1-U1 (DEC-399): the cash drawer sheet. Hidden unless the location's cash setting is on or a drawer is open.
      {
        id: CASH_DRAWER_ROW_ID,
        label: "Cash drawer",
        testId: "account-menu-cash-drawer",
        permissions: ["drawer.open", "drawer.close"],
        sub: "Open or close",
        word: "cash drawer",
      },
      {
        id: "match-clients",
        label: "Match clients",
        testId: "account-menu-match-clients",
        permissions: ["guest.reconciliation.view"],
        sub: "Possible duplicates",
        word: "clients",
      },
      // RPTLAYOUT1-F1-S2: the read only possible duplicate clients list, beside Match clients (it was parked in Reports).
      {
        id: "possible-duplicates",
        label: "Possible duplicates",
        testId: "account-menu-possible-duplicates",
        permissions: ["guest.directory.duplicates.view"],
        sub: "Read only list",
        word: "clients",
      },
    ],
  },
];

/** The first level rows that are not groups, with the permission each needs
 * (undefined means everyone signed in). Settings is gated separately by
 * ui/src/settingsGate.ts's SETTINGS_ENTRY_PERMISSIONS. */
export const PAYMENT_TERMINAL_PERMISSION = "floor.terminal.select";

export type PermissionCheck = (permissionId: string) => boolean;

/** INV38: the row id of Set starting stock, hidden from the menu once starting
 * stock is set for every item. */
export const STARTING_STOCK_ROW_ID = "starting-stock";

/** The group without the rows in `hiddenIds`. The rows are the same objects, so a
 * caller can pass the result to visibleItems, groupHint and groupBadge and test
 * `visible.includes(item)` against the full group. Permission is a separate
 * question: a hidden row never becomes visible to someone without the permission. */
export function withoutHiddenRows(group: MenuGroup, hiddenIds: ReadonlySet<string>): MenuGroup {
  if (hiddenIds.size === 0) return group;
  return { ...group, items: group.items.filter((item) => !hiddenIds.has(item.id)) };
}

export function visibleItems(group: MenuGroup, has: PermissionCheck): MenuItem[] {
  return group.items.filter((item) => item.permissions.some((id) => has(id)));
}

/** Whether the group's row shows: a hub group when any of its rows does, a direct group (Reports) when any of its ids is held. */
export function groupVisible(group: MenuGroup, has: PermissionCheck): boolean {
  return group.direct !== undefined
    ? group.direct.permissions.some((id) => has(id))
    : visibleItems(group, has).length > 0;
}

/** The groups that still have at least one visible row: an empty group is
 * hidden, never shown with nothing in it. */
export function visibleGroups(has: PermissionCheck): MenuGroup[] {
  return ACCOUNT_MENU_GROUPS.filter((group) => groupVisible(group, has));
}

/** The line under the group name on the first level: the short words of the
 * rows the person can actually open, in order, without repeats ("Cart, orders,
 * outside orders, price files"). A person who holds only some rows sees only
 * their words, so the hint never promises a screen that is not there. */
export function groupHint(group: MenuGroup, has: PermissionCheck): string {
  if (group.direct !== undefined) return groupVisible(group, has) ? group.direct.hint : "";
  const words: string[] = [];
  for (const item of visibleItems(group, has)) {
    if (item.word !== undefined && !words.includes(item.word)) {
      words.push(item.word);
    }
  }
  const text = words.join(", ");
  return text.length === 0 ? "" : text.charAt(0).toUpperCase() + text.slice(1);
}

export interface MenuBadge {
  readonly count: number;
  readonly tone: BadgeTone;
}

const TONE_RANK: Record<BadgeTone, number> = { red: 3, orange: 2, blue: 1 };

/** The one badge a group row carries: the SUM of the counts on the group's VISIBLE rows, in the most urgent tone among
 * them (red beats orange beats blue). A zero count is no badge. MGRDAYMAJ1 (manager-day-04): the group rows, the name
 * chip and the Needs you strip are one count read from one place, so the number on the chip is the sum of the numbers
 * on the group rows, and a group's number is the sum of the numbers on the rows inside it. */
export function groupBadge(
  group: MenuGroup,
  has: PermissionCheck,
  badges: Readonly<Record<string, MenuBadge | undefined>>,
): MenuBadge | undefined {
  let total = 0;
  let tone: BadgeTone | undefined;
  for (const item of visibleItems(group, has)) {
    const badge = badges[item.id];
    if (badge === undefined || badge.count <= 0) continue;
    total += badge.count;
    if (tone === undefined || TONE_RANK[badge.tone] > TONE_RANK[tone]) {
      tone = badge.tone;
    }
  }
  return tone === undefined ? undefined : { count: total, tone };
}

/** The count on the name chip: every badge on a visible menu row, summed, whatever its tone. The same number as the
 * group rows added together, and as the rows of the Needs you strip. */
export function chipBadgeCount(
  has: PermissionCheck,
  badges: Readonly<Record<string, MenuBadge | undefined>>,
): number {
  let total = 0;
  for (const group of ACCOUNT_MENU_GROUPS) {
    const badge = groupBadge(group, has, badges);
    if (badge !== undefined) total += badge.count;
  }
  return total;
}

/** MGRDAYMAJ1 (manager-day-04): one row of the Needs you strip on Open Tabs, built from the same badges the menu draws. */
export interface NeedsYouRow {
  /** The menu row this count belongs to; the app resolves it to the same screen the menu row opens. */
  readonly rowId: string;
  readonly text: string;
  readonly tone: BadgeTone;
  readonly count: number;
}

const NEEDS_YOU_WORDS: Readonly<Record<string, (count: number) => string>> = {
  closeout: () => "Yesterday is not finalized",
  "manager-exceptions": (count) => `${count} manager ${count === 1 ? "exception" : "exceptions"}`,
  orders: (count) => `${count} ${count === 1 ? "order is" : "orders are"} waiting for approval`,
  "match-clients": (count) => `${count} ${count === 1 ? "client" : "clients"} to match`,
  transfers: (count) => `${count} ${count === 1 ? "transfer" : "transfers"} to receive`,
  "count-stock": () => "A stock count is in progress",
  cart: (count) => `${count} ${count === 1 ? "line" : "lines"} in the purchasing cart`,
};

/** The rows of the Needs you strip: one per visible menu row that carries a badge, most urgent first, in menu order
 * within a tone. Their counts add up to chipBadgeCount, by construction (both read the same visible rows). */
export function needsYouRows(
  has: PermissionCheck,
  badges: Readonly<Record<string, MenuBadge | undefined>>,
  /** Words that replace the default for a row, when the caller knows more (the closeout row names the day). */
  words: Readonly<Record<string, string>> = {},
): NeedsYouRow[] {
  const rows: NeedsYouRow[] = [];
  for (const group of ACCOUNT_MENU_GROUPS) {
    for (const item of visibleItems(group, has)) {
      const badge = badges[item.id];
      const sentence = NEEDS_YOU_WORDS[item.id];
      if (badge === undefined || badge.count <= 0 || sentence === undefined) continue;
      rows.push({ rowId: item.id, text: words[item.id] ?? sentence(badge.count), tone: badge.tone, count: badge.count });
    }
  }
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => TONE_RANK[b.row.tone] - TONE_RANK[a.row.tone] || a.index - b.index)
    .map((entry) => entry.row);
}

/** The body of GET /tenants/:tenantId/menu-counts (UX80-F1). A key is ABSENT when the session may
 * not see that count; the client treats an absent key and a zero alike, as no badge. */
export interface MenuCountsBody {
  readonly cartLines?: number;
  readonly ordersAwaitingApproval?: number;
  readonly ordersAwaitingYourApproval?: number;
  readonly clientsToMatch?: number;
  readonly countsInProgress?: number;
  /** MGRDAYMAJ1: the most recent past business day with orders and no finalization (YYYY-MM-DD), added by the menu-counts route. */
  readonly unfinalizedBusinessDate?: string;
  /** INVXFER1-S2: transfers Sent to this location and not yet received. Added by the client from the transfers list, not
   * by the menu-counts route. */
  readonly transfersToReceive?: number;
}

/** Turns the counts body into the per-row badges the menu draws. Orange is waiting work (orders
 * waiting for approval, clients to match), blue is in progress (cart lines, counts in progress);
 * red is reserved for manager exceptions, which keep their own read. A zero, a negative, a
 * fractional or an absent count draws nothing. */
export function menuBadgesFromCounts(
  counts: MenuCountsBody,
): Record<string, MenuBadge | undefined> {
  const out: Record<string, MenuBadge | undefined> = {};
  const add = (rowId: string, count: number | undefined, tone: BadgeTone): void => {
    if (count !== undefined && Number.isInteger(count) && count > 0) {
      out[rowId] = { count, tone };
    }
  };
  add("cart", counts.cartLines, "blue");
  add("orders", Math.max(counts.ordersAwaitingApproval ?? 0, counts.ordersAwaitingYourApproval ?? 0), "orange");
  add("match-clients", counts.clientsToMatch, "orange");
  add("count-stock", counts.countsInProgress, "blue");
  add("transfers", counts.transfersToReceive, "blue");
  // MGRDAYMAJ1: a past day that is over and not finalized is one thing for the manager to do, on the Closeout summary row.
  add("closeout", counts.unfinalizedBusinessDate === undefined ? undefined : 1, "red");
  return out;
}
